import {decrementIssuesTabCounter} from '@github-ui/issues-tab-counter'
import {useMutation, type UseMutationResult} from '@github-ui/react-query'
import {deleteIssueMutation as deleteIssueMutationQueryId} from '@github-ui/server-persisted-queries'
import type {DeleteIssueMutationMutation} from '@github-ui/server-persisted-queries/types/deleteIssueMutation'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {deleteIssueMutation} from './__generated__/deleteIssueMutation.graphql'
import {issueLifecyclePath, submitIssueLifecycleMutation} from './issue-lifecycle-mutation'

export function commitDeleteIssueMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {issueId: string}
  onCompleted?: () => void
  onError?: (error: Error) => void
}) {
  return commitMutation<deleteIssueMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation deleteIssueMutation($input: DeleteIssueInput!) @raw_response_type {
        deleteIssue(input: $input) {
          issue {
            id
          }
        }
      }
    `,
    variables: {
      input,
    },
    onCompleted: (response, errors) => {
      const errorMessage = errors?.[0]?.message
      if (errorMessage || !response.deleteIssue?.issue) {
        onError?.(new Error(errorMessage ?? 'Issue deletion failed'))
        return
      }
      onCompleted?.()
      decrementIssuesTabCounter()
    },
    onError: error => onError?.(error),
  })
}

export function useDeleteIssueMutation({
  owner,
  repository,
  number,
  onCompleted,
  onError,
}: {
  owner: string
  repository: string
  number: number
  onCompleted?: () => void
  onError?: (error: Error) => void
}): UseMutationResult<DeleteIssueMutationMutation, Error, void> {
  return useMutation({
    mutationFn: () => submitDeleteIssueMutation({owner, repository, number}),
    onSuccess: () => {
      onCompleted?.()
      decrementIssuesTabCounter()
    },
    onError,
  })
}

export function submitDeleteIssueMutation({
  owner,
  repository,
  number,
}: {
  owner: string
  repository: string
  number: number
}) {
  return submitIssueLifecycleMutation<DeleteIssueMutationMutation>({
    path: `${issueLifecyclePath(owner, repository, number)}.json`,
    method: 'DELETE',
    queryId: deleteIssueMutationQueryId,
    errorMessage: 'Issue deletion failed',
    isSuccess: data => data.deleteIssue?.issue != null,
  })
}
